import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import NcImage from "shared/NcImage/NcImage";
import { getIconComponent } from "shared/Icon/IconDymamiclisting";
import { Sparkles, Grid3x3, ChevronRight } from "lucide-react";

interface AmenitiesFeaturesProps {
  propertyData: PropertyToEdit | null;
  openModalAmenities: () => void;
  isOpenModalAmenities: boolean;
  closeModalAmenities: () => void;
}

const AmenitiesFeatures: React.FC<AmenitiesFeaturesProps> = ({
  propertyData,
  openModalAmenities,
  isOpenModalAmenities,
  closeModalAmenities,
}) => {
  if (!propertyData) return null;

  const data = [
    ...(propertyData.features || []),
    ...(propertyData.amenities?.map((amenity) => ({
      ...amenity,
      feature_type: "amenity",
    })) || []),
  ];

  const showViewMore = data.length > 9;
  const displayItems = showViewMore ? data.slice(0, 9) : data;

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
            <Sparkles size={24} className="text-[#f5694b] dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              What this place offers
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {data.length} {data.length === 1 ? 'amenity' : 'amenities'} & features available
            </p>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Amenities Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayItems?.map((item) => {
          const Icon = getIconComponent(item?.icon_name || "");

          return (
            <div
              key={item.id}
              className="group flex items-center gap-3 p-4 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-md transition-all"
            >
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-900/30 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                {item.image ? (
                  <NcImage
                    src={item?.image}
                    alt={item?.name}
                    className="w-6 h-6 object-cover rounded"
                  />
                ) : (
                  <Icon className="text-xl text-[#f5694b] dark:text-orange-400" />
                )}
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 line-clamp-1">
                {item?.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* View More Button */}
      {showViewMore && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={openModalAmenities}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-700 hover:border-[#f5694b] dark:hover:border-[#f5694b] hover:text-[#f5694b] transition-all shadow-sm hover:shadow-md"
          >
            <Grid3x3 size={18} />
            Show all {data.length} amenities
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Modal */}
      <Transition appear show={isOpenModalAmenities} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModalAmenities}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
            </Transition.Child>

            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block py-8 h-screen w-full max-w-4xl">
                <div className="inline-flex pb-2 flex-col w-full text-left align-middle transition-all transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 shadow-2xl h-full">
                  {/* Modal Header */}
                  <div className="relative flex-shrink-0 px-6 py-5 border-b border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <Sparkles size={20} className="text-[#f5694b] dark:text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          All amenities & features
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {data.length} items available at this property
                        </p>
                      </div>
                    </div>
                    <span className="absolute right-4 top-4">
                      <ButtonClose onClick={closeModalAmenities} />
                    </span>
                  </div>

                  {/* Modal Content */}
                  <div className="px-6 py-4 overflow-auto flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {data?.map((item) => {
                        const Icon = getIconComponent(item?.icon_name || "");

                        return (
                          <div
                            key={item?.id}
                            className="flex items-center gap-3 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 hover:border-orange-300 dark:hover:border-orange-700 transition-all"
                          >
                            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-900/30">
                              {item?.image ? (
                                <NcImage
                                  src={item?.image}
                                  alt={item?.name}
                                  className="w-6 h-6 object-cover rounded"
                                />
                              ) : (
                                <Icon className="text-xl text-[#f5694b] dark:text-orange-400" />
                              )}
                            </div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {item?.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/50">
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      All amenities are subject to availability and may vary by room or season
                    </p>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default AmenitiesFeatures;